"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, type OfflineMesure, type OfflineCampagne } from "@/core/offline/db";

export interface PreviousMesureInfo {
  mesure: OfflineMesure;
  campagne: OfflineCampagne;
}

/**
 * Retourne la mesure du pin dans la campagne clôturée la plus récente
 * AVANT la campagne courante (par date de campagne), le cas échéant.
 *
 * Utile pour afficher dans la fiche de saisie la valeur précédente en
 * rappel discret (sous les champs qui évoluent : min, max).
 *
 * Renvoie `undefined` si :
 * - aucune campagne précédente clôturée
 * - pas de mesure sur ce pin dans la campagne précédente (pin ajouté
 *   après la campagne précédente)
 */
export function usePreviousMesure(
  pinId: string | undefined,
  currentCampagneId: string | undefined
): PreviousMesureInfo | undefined {
  return useLiveQuery(
    async () => {
      if (!pinId || !currentCampagneId) return undefined;

      const current = await offlineDb.campagnes.get(currentCampagneId);
      if (!current) return undefined;

      // Toutes les campagnes clôturées du site, triées par date desc
      const closed = await offlineDb.campagnes
        .where("siteId")
        .equals(current.siteId)
        .filter((c) => c.status === "cloturee")
        .sortBy("date");
      closed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Chercher la première campagne strictement antérieure à `current` en DATE.
      // Si `current` est active, on prend simplement la plus récente clôturée.
      const currentTime = new Date(current.date).getTime();
      const previousCampagne = closed.find(
        (c) => new Date(c.date).getTime() < currentTime
      );
      if (!previousCampagne) return undefined;

      // Mesure du pin dans cette campagne précédente
      const prevMesure = await offlineDb.mesures
        .where("pinId")
        .equals(pinId)
        .filter((m) => m.campagneId === previousCampagne.id)
        .first();
      if (!prevMesure) return undefined;

      return { mesure: prevMesure, campagne: previousCampagne };
    },
    [pinId, currentCampagneId]
  );
}
